import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { memberSummaryApi } from '@formosa/shared/api/im';
import { journeyConversationApi } from '../../api/journeyConversationApi';
import { journeyApi } from '../../api/journeyApi';
import { classifyJourneyAccessError, normalizeJourneyWorkspace } from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import { TravelThreadView } from '../messages/TravelThreadPage';
import { asId, indexSummaries } from '../messages/travelChatModel';
import {
  canEnsureJourneyChat,
  isJourneyConversationAbsent,
  isTerminalJourneyChatLifecycle,
  journeyChatGateCopy,
  normalizeJourneyConversation,
  participantContextLine,
  terminalJourneyChatCopy,
} from './journeyChatModel';
import '../messages/travel-messages.css';

export default function JourneyChatPage() {
  const { journeyId: routeId } = useParams();
  const journeyId = asId(routeId);
  const generation = useRef(0);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [journey, setJourney] = useState(null);
  const [binding, setBinding] = useState(null);
  const [summaries, setSummaries] = useState({});
  const [ensuring, setEnsuring] = useState(false);

  const load = useCallback(async () => {
    const requestGeneration = ++generation.current;
    if (!/^[1-9]\d*$/.test(journeyId)) {
      setPhase('not-found');
      return;
    }
    setPhase('loading');
    setError('');
    try {
      const detail = normalizeJourneyWorkspace(await journeyApi.getDetail(journeyId));
      if (requestGeneration !== generation.current) return;
      if (asId(detail.journeyId) !== journeyId) {
        setPhase('not-found');
        return;
      }
      setJourney(detail);
      let nextBinding = null;
      try {
        nextBinding = normalizeJourneyConversation(await journeyConversationApi.get(journeyId));
      } catch (cause) {
        if (!isJourneyConversationAbsent(cause)) throw cause;
      }
      if (requestGeneration !== generation.current) return;
      setBinding(nextBinding);
      setPhase('ready');
      const memberIds = (detail.members || [])
        .filter((member) => member.membershipStatus === 'ACTIVE')
        .map((member) => asId(member.memberId));
      if (memberIds.length) {
        try {
          const rows = await memberSummaryApi.batch([...new Set(memberIds)]);
          if (requestGeneration !== generation.current) return;
          setSummaries(indexSummaries(rows));
        } catch {
          /* names optional */
        }
      }
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      const access = classifyJourneyAccessError(cause);
      if (access === 'not-found') setPhase('not-found');
      else if (access === 'forbidden') setPhase('forbidden');
      else {
        setError(cause?.message || '無法載入旅程聊天室。');
        setPhase('error');
      }
    }
  }, [journeyId]);

  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);

  async function openChat() {
    if (ensuring || !canEnsureJourneyChat(journey?.lifecycleStatus)) return;
    setEnsuring(true);
    setError('');
    try {
      const created = normalizeJourneyConversation(await journeyConversationApi.ensure(journeyId));
      setBinding(created);
    } catch (cause) {
      setError(cause?.message || '無法開啟旅程聊天室。');
    } finally {
      setEnsuring(false);
    }
  }

  if (phase === 'loading') {
    return (
      <main className="travel-messages-state" aria-live="polite">
        <div className="explore-spinner" />
        <p>載入旅程聊天室…</p>
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="travel-messages-state">
        <h1>找不到這趟旅程</h1>
        <p>連結可能已失效。</p>
        <Link to="/journeys">回到我的旅程</Link>
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="travel-messages-state">
        <h1>沒有權限</h1>
        <p>只有旅程成員可以進入旅程聊天室。</p>
        <Link to="/journeys">回到我的旅程</Link>
      </main>
    );
  }
  if (phase === 'error') {
    return (
      <main className="travel-messages-state" role="alert">
        <h1>無法載入旅程聊天室</h1>
        <p>{error}</p>
        <button type="button" onClick={load}>再試一次</button>
      </main>
    );
  }

  const conversationId = binding?.conversationId || '';
  const activeMemberIds = (journey.members || [])
    .filter((member) => member.membershipStatus === 'ACTIVE')
    .map((member) => asId(member.memberId));
  const participantLine = participantContextLine(summaries, activeMemberIds);
  const workspaceHref = `/journeys/${journeyId}`;
  const gate = journeyChatGateCopy(journey.lifecycleStatus, Boolean(conversationId));

  if (conversationId) {
    const terminal = isTerminalJourneyChatLifecycle(journey.lifecycleStatus);
    return (
      <div className="journey-chat" data-testid="journey-chat">
        <JourneyWorkspaceNav journeyId={journey.journeyId} />
        <TravelThreadView
          conversationId={conversationId}
          backTo={workspaceHref}
          backLabel="回旅程"
          heading={journey.title || '旅程'}
          kicker="旅程聊天室"
          participantLine={participantLine}
          readOnly={terminal}
          readOnlyNotice={terminal ? terminalJourneyChatCopy(journey.lifecycleStatus) : ''}
        />
      </div>
    );
  }

  return (
    <div className="journey-chat journey-chat--gate" data-testid="journey-chat">
      <JourneyWorkspaceNav journeyId={journey.journeyId} />
      <main className="travel-messages-state">
        <p className="travel-thread__kicker">旅程聊天室</p>
        <h1>{journey.title || '旅程'}</h1>
        {participantLine ? <p>{participantLine}</p> : null}
        <h2>{gate.title}</h2>
        <p>{gate.body}</p>
        {error ? <p role="alert">{error}</p> : null}
        {gate.action === 'ensure' ? (
          <button type="button" onClick={openChat} disabled={ensuring} aria-busy={ensuring}>
            {ensuring ? '開啟中…' : gate.button}
          </button>
        ) : null}
        <Link to={workspaceHref}>回旅程</Link>
      </main>
    </div>
  );
}
